import React, { useState } from "react";
import { useGetFormByIdPublicQuery, useSubmitFormMutation } from "../api/businessApi";
import { CheckCircle, AlertCircle } from "lucide-react";
import JotFormEmbed, { getJotFormId } from "./JotFormEmbed";

// Renders a single form field
function FormField({ field, value, onChange, error }) {
  const baseClass =
    "w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#135879] transition " +
    (error ? "border-red-400 bg-red-50" : "border-gray-300");

  switch (field.type) {
    case "textarea":
      return (
        <textarea
          rows={4}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          className={baseClass + " resize-none"}
        />
      );

    case "radio":
      return (
        <div className="space-y-2">
          {(field.options || []).map((opt) => (
            <label key={opt} className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
              <input
                type="radio"
                name={field.id}
                value={opt}
                checked={value === opt}
                onChange={() => onChange(opt)}
                className="accent-[#135879]"
              />
              {opt}
            </label>
          ))}
        </div>
      );

    case "checkbox":
      return (
        <div className="space-y-2">
          {(field.options || []).map((opt) => {
            const checked = Array.isArray(value) && value.includes(opt);
            return (
              <label key={opt} className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const current = Array.isArray(value) ? value : [];
                    onChange(checked ? current.filter((v) => v !== opt) : [...current, opt]);
                  }}
                  className="accent-[#135879] rounded"
                />
                {opt}
              </label>
            );
          })}
        </div>
      );

    case "select":
      return (
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={baseClass}
        >
          <option value="">— Select —</option>
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );

    default:
      return (
        <input
          type={field.type === "email" ? "email" : field.type === "phone" ? "tel" : field.type === "number" ? "number" : "text"}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          className={baseClass}
        />
      );
  }
}

// ─── Custom Form Renderer ─────────────────────────────────────────────────────
function CustomForm({ formId, resourceId }) {
  const { data: form, isLoading } = useGetFormByIdPublicQuery(formId);
  const [submitForm, { isLoading: isSubmitting }] = useSubmitFormMutation();
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  if (isLoading) return <div className="text-center py-8 text-gray-400 text-sm">Loading form...</div>;
  if (!form) return null;

  const handleChange = (fieldId, value) => {
    setValues((prev) => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) setErrors((prev) => ({ ...prev, [fieldId]: null }));
  };

  const validate = () => {
    const newErrors = {};
    for (const field of form.fields || []) {
      if (field.required) {
        const val = values[field.id];
        const empty = val === undefined || val === null || val === "" || (Array.isArray(val) && val.length === 0);
        if (empty) newErrors[field.id] = `${field.label} is required`;
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitError(null);
    try {
      await submitForm({ formId, data: values, resource_id: resourceId }).unwrap();
      setSubmitted(true);
    } catch (err) {
      setSubmitError("Something went wrong. Please try again.");
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-10">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
        <h3 className="text-xl font-semibold text-gray-800 mb-1">Thank you!</h3>
        <p className="text-gray-500 text-sm">Your response has been received. We'll be in touch.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {form.description && (
        <p className="text-gray-600 text-sm leading-relaxed">{form.description}</p>
      )}

      {(form.fields || []).map((field) => (
        <div key={field.id}>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            {field.label}
            {field.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          <FormField
            field={field}
            value={values[field.id]}
            onChange={(val) => handleChange(field.id, val)}
            error={errors[field.id]}
          />
          {errors[field.id] && (
            <p className="text-xs text-red-500 mt-1">{errors[field.id]}</p>
          )}
        </div>
      ))}

      {submitError && (
        <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <AlertCircle size={16} />
          {submitError}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-[#135879] hover:bg-[#0e4562] text-white font-semibold py-3 rounded-lg transition disabled:opacity-60"
      >
        {isSubmitting ? "Submitting..." : "Submit"}
      </button>
    </form>
  );
}

// ─── Main ResourceForm Component ──────────────────────────────────────────────
const ResourceForm = ({ resource }) => {
  const hasCustomForm = resource.form_id;
  const hasGoogleForm = resource.external_form_url?.trim();

  if (!hasCustomForm && !hasGoogleForm) return null;

  return (
    <div className="mt-10 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-[#135879] to-[#0e4562] px-6 py-4">
        <h2 className="text-white font-semibold text-lg">Express Your Interest</h2>
        <p className="text-white/70 text-sm mt-0.5">Fill out the form below to participate</p>
      </div>

      <div className="p-6 md:p-8">
        {hasCustomForm ? (
          <CustomForm formId={resource.form_id} resourceId={resource.id} />
        ) : getJotFormId(hasGoogleForm) ? (
          <JotFormEmbed
            url={hasGoogleForm}
            formId={getJotFormId(hasGoogleForm)}
            title={`${resource.title} — registration form`}
          />
        ) : (
          <iframe
            src={hasGoogleForm}
            title="Response Form"
            frameBorder="0"
            marginHeight="0"
            marginWidth="0"
            className="rounded-lg w-full min-h-[480px] h-[60vh] md:h-[800px]"
          >
            Loading form…
          </iframe>
        )}
      </div>
    </div>
  );
};

export default ResourceForm;
